import { useState, useEffect } from 'react';
import { preloadImages, preloadFonts, preloadVideos } from '../utils/assetPreloader';

interface AssetPreloaderResult {
  isLoading: boolean;
  progress: number;
  error: Error | null;
}

const IMAGES_TO_PRELOAD = [
  '/assets/webpage_background.png',
  '/assets/CATERVA_OD.jpg',
  '/assets/Kimera_Cargando.png',
  '/assets/Kimeracreatura.png',
  '/assets/KimeraWare_logo.png',
  '/assets/Rectangulos_KW.png',
  '/assets/SanMartin_ENG.webp',
  '/assets/SanMartin_ES.webp',
  '/assets/steam_logo_basic.png',
  '/assets/steam_logo_hover.png',
  '/assets/kimeraware_televisor.png',
  '/assets/off_Screen.png',
  '/assets/KimeraWare.gif',
  '/assets/caterva/2026-07-20_02-33-24__1_.gif',
  '/assets/caterva/control01.gif',
  '/assets/caterva/millo_trim.gif',
  '/assets/caterva/proyecto_convertir_a_gif_-_28_de_julio_.gif',
  '/assets/caterva/sleepy_eyes.gif',
  '/assets/san_martin/gif_1.gif',
  '/assets/san_martin/gif_2.gif',
  '/assets/san_martin/gif_3.gif',
  '/assets/san_martin/imagen_2.png',
  '/assets/san_martin/san_martin_poster_3.png',
  '/assets/controls/ON.png',
  '/assets/controls/OFF.png',
  '/assets/controls/ARRIBA.png',
  '/assets/controls/abajo.png',
  '/assets/controls/MUTE.png',
  '/assets/controls/SOUND.png',
  '/assets/controls/STEAM.png',
  '/assets/controls/steam_apagado.png',
  '/assets/QUE_ES_KIMERAWARE_ENG.png',
  '/assets/QUE_ES_KIMERAWARE.png',
  '/assets/Personajes.png',
  '/assets/IG.png',
  '/assets/SC.png',
  '/assets/TW.png',
  '/assets/YT.png',
  '/assets/avatars/zebitas_pfp.jpg',
  '/assets/avatars/tvnauta_pfp.jpg',
  '/assets/avatars/angelito_pfp.jpg',
  '/assets/avatars/arziel_pfp.jpg',
  '/assets/avatars/cuki_pfp.jpg',
  '/assets/avatars/default_pic.png',
  '/assets/avatars/exxhy.png',
  '/assets/avatars/kev_pfp.jpg',
  '/assets/avatars/lzcero_pfp.jpg',
  '/assets/avatars/neo_pic.png',
  '/assets/avatars/paula_pfp.png',
  '/assets/avatars/shibi_pfp.jpg',
  '/assets/avatars/system_pfp.jpg',
  '/assets/avatars/xlito_pfp.jpg',
];

const VIDEOS_TO_PRELOAD = [
  '/assets/video/static.mp4',
  '/assets/video/caterva_trailer.mp4',
  '/assets/video/san_martin_trailer.mp4',
  '/assets/video/tumama.mp4',
];

const FONTS_TO_PRELOAD = [
  { name: 'Bungee', url: '/fonts/Bungee-Regular.ttf' },
  { name: 'SyneMono', url: '/fonts/SyneMono-Regular.ttf' },
];

const MAX_LOADING_TIME = 60000;
const IMAGE_WEIGHT = 0.6;
const VIDEO_WEIGHT = 0.2;

export const useAssetPreloader = (): AssetPreloaderResult => {
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [progress, setProgress] = useState<number>(0);
  const [error, setError] = useState<Error | null>(null);

  useEffect(() => {
    let mounted = true;

    const loadAssets = async (): Promise<void> => {
      try {
        const imagePromise = preloadImages(IMAGES_TO_PRELOAD).then(() => {
          if (mounted) {
            setProgress(IMAGE_WEIGHT * 100);
          }
        });

        const videoPromise = preloadVideos(VIDEOS_TO_PRELOAD).then(() => {
          if (mounted) {
            setProgress((IMAGE_WEIGHT + VIDEO_WEIGHT) * 100);
          }
        });

        const fontPromise = preloadFonts(FONTS_TO_PRELOAD);

        await Promise.all([imagePromise, videoPromise, fontPromise]);

        if (mounted) {
          setProgress(100);
          setIsLoading(false);
        }
      } catch (err) {
        if (mounted) {
          setError(err instanceof Error ? err : new Error('Failed to load assets'));
          setIsLoading(false);
        }
      }
    };

    const timeout = setTimeout(() => {
      if (mounted) {
        setIsLoading(false);
      }
    }, MAX_LOADING_TIME);

    loadAssets();

    return () => {
      mounted = false;
      clearTimeout(timeout);
    };
  }, []);

  return { isLoading, progress, error };
};
